/**
 * Agent tools + tool credentials API service.
 *
 * Two related concerns:
 *  - **Bindings** are per-agent: which tools an agent has enabled and their
 *    non-secret config (lead fields, discovery depth, Jira project, …).
 *  - **Credentials** are per-tenant per-tool: encrypted secrets (e.g. Jira API
 *    token). The API only ever returns metadata (which keys are set), never the
 *    secret values — so they're shared by every agent in the tenant.
 *
 * Pure async functions — no React state, no hooks.
 */
import { get, put, del } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';

// ── Domain types ──────────────────────────────────────────────────────────────

/** A JSON-Schema-ish shape; we only read it loosely in the UI. */
export interface ToolParameterSchema {
  type: string;
  properties?: Record<string, unknown>;
  required?: string[];
  [key: string]: unknown;
}

/** Catalog tool merged with this agent's binding state. */
export interface AgentToolView {
  name: string;
  description: string;
  parameters: ToolParameterSchema;
  isBound: boolean;
  isEnabled: boolean;
  config: Record<string, unknown>;
  /** Routing metadata for the policy UI (P5). */
  capability?: string | null;
  defaultUseWhen?: string | null;
}

export interface SetBindingPayload {
  isEnabled: boolean;
  config?: Record<string, unknown>;
}

/** Safe credential metadata — which keys are set, never their values. */
export interface ToolCredentialMetadata {
  toolName: string;
  keyNames: string[];
  configured?: boolean;
  configuredAt?: string;
}

// ── Service ───────────────────────────────────────────────────────────────────

export const agentToolsService = {
  /** Catalog merged with the agent's bindings — the configuration view. */
  listForAgent(agentId: string): Promise<AgentToolView[]> {
    return get<AgentToolView[]>(endpoints.agentTools.list(agentId));
  },

  /** Enable/disable a tool for the agent and set its per-binding config. */
  setBinding(
    agentId: string,
    toolName: string,
    payload: SetBindingPayload,
  ): Promise<unknown> {
    return put(endpoints.agentTools.binding(agentId, toolName), {
      isEnabled: payload.isEnabled,
      config: payload.config ?? {},
    });
  },

  /** Remove an agent's binding for a tool (reverts to the tool default). */
  removeBinding(agentId: string, toolName: string): Promise<void> {
    return del(endpoints.agentTools.binding(agentId, toolName));
  },

  /** Which secret keys are currently set for a tool (per-agent). */
  getCredentials(
    agentId: string,
    toolName: string,
  ): Promise<ToolCredentialMetadata> {
    return get<ToolCredentialMetadata>(
      endpoints.tools.credentials(agentId, toolName),
    );
  },

  /** Store/replace the tool's secrets (per-agent). */
  setCredentials(
    agentId: string,
    toolName: string,
    secrets: Record<string, string>,
  ): Promise<ToolCredentialMetadata> {
    return put<ToolCredentialMetadata>(
      endpoints.tools.credentials(agentId, toolName),
      { secrets },
    );
  },

  /** Delete the tool's stored secrets (per-agent). */
  deleteCredentials(agentId: string, toolName: string): Promise<void> {
    return del(endpoints.tools.credentials(agentId, toolName));
  },
} as const;
